import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Upload, FileText, Loader2, Check, AlertCircle, ArrowRight, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABEL = {
    update: "Mise à jour",
    unchanged: "Inchangé",
    new: "Nouvel article",
};

const KIND_COLOR = {
    update: { color: "#D4BC7C", border: "var(--bc-gold)", bg: "rgba(212,188,124,0.06)" },
    unchanged: { color: "#A0A0A0", border: "var(--bc-border)", bg: "transparent" },
    new: { color: "#93c5fd", border: "#93c5fd", bg: "rgba(147,197,253,0.06)" },
};

const formatPrice = (v, unit = "kg") => {
    if (v == null || v === "") return "Sur demande";
    return `CHF ${Number(v).toFixed(2)} / ${unit || "kg"}`;
};

/**
 * Modal orchestrating the two-step import: (1) upload+preview, (2) confirm+apply.
 */
export default function TarifsImportModal({ open, onClose }) {
    const qc = useQueryClient();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null); // "pending" | "done" | "error"
    const [jobError, setJobError] = useState(null);
    // Per-row selection state:
    //   updates: Map<pdfIdx, boolean>   (only for kind=update, default true)
    //   creates: Map<pdfIdx, categoryId> (only for kind=new, category selection)
    const [updateSel, setUpdateSel] = useState({});
    const [createCats, setCreateCats] = useState({});

    const { data: categories = [] } = useQuery({
        queryKey: ["admin-categories"],
        queryFn: async () => (await api.get("/admin/categories")).data,
        enabled: open,
    });

    const pollRef = useRef(null);

    // Cleanup poll timer on unmount / close
    useEffect(() => {
        return () => {
            if (pollRef.current) clearTimeout(pollRef.current);
        };
    }, []);

    const handlePreviewReady = (data) => {
        setPreview(data);
        setJobStatus("done");
        const nextUpdate = {};
        data.rows.forEach((r, idx) => {
            if (r.kind === "update") nextUpdate[idx] = true;
        });
        setUpdateSel(nextUpdate);
        setCreateCats({});
    };

    const startPolling = (id) => {
        let attempts = 0;
        const MAX_ATTEMPTS = 90; // 90 × 2s = 3 min ceiling
        const tick = async () => {
            attempts += 1;
            try {
                const res = await api.get(`/admin/tarifs/import-jobs/${id}`);
                const doc = res.data;
                if (doc.status === "done" && doc.preview) {
                    handlePreviewReady(doc.preview);
                    return;
                }
                if (doc.status === "error") {
                    setJobStatus("error");
                    setJobError(doc.error || "Erreur inconnue durant l'extraction.");
                    toast.error(doc.error || "L'extraction du PDF a échoué.");
                    return;
                }
                if (attempts >= MAX_ATTEMPTS) {
                    setJobStatus("error");
                    setJobError("Délai dépassé (3 min). Merci de réessayer avec un PDF plus léger.");
                    toast.error("Délai d'analyse dépassé — réessayez.");
                    return;
                }
                pollRef.current = setTimeout(tick, 2000);
            } catch (e) {
                setJobStatus("error");
                setJobError(formatApiError(e));
                toast.error(formatApiError(e));
            }
        };
        pollRef.current = setTimeout(tick, 1500);
    };

    const uploadMutation = useMutation({
        mutationFn: async (f) => {
            const form = new FormData();
            form.append("file", f);
            // IMPORTANT: do NOT set Content-Type manually — axios must auto-generate
            // it with the correct boundary parameter for the multipart body to parse.
            // Also override the axios instance default (application/json) with `undefined`
            // so axios lets the browser fill in the correct multipart/form-data; boundary=...
            const r = await api.post("/admin/tarifs/import-start", form, {
                headers: { "Content-Type": undefined },
                timeout: 60_000,
                transformRequest: (data) => data, // prevent axios from JSON-stringifying FormData
            });
            return r.data;
        },
        onSuccess: (data) => {
            setJobId(data.job_id);
            setJobStatus("pending");
            setJobError(null);
            startPolling(data.job_id);
        },
        onError: (e) => {
            setJobStatus("error");
            setJobError(formatApiError(e));
            toast.error(formatApiError(e));
        },
    });

    const applyMutation = useMutation({
        mutationFn: (payload) => api.post("/admin/tarifs/import-apply", payload),
        onSuccess: (res) => {
            const { updated = 0, created = 0 } = res.data || {};
            toast.success(`Import réussi — ${updated} mis à jour, ${created} créé${created > 1 ? "s" : ""}.`);
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
            close();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const close = () => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        setFile(null);
        setPreview(null);
        setJobId(null);
        setJobStatus(null);
        setJobError(null);
        setUpdateSel({});
        setCreateCats({});
        onClose?.();
    };

    const startUpload = () => {
        if (!file) {
            toast.error("Sélectionnez d'abord un PDF.");
            return;
        }
        setJobStatus(null);
        setJobError(null);
        setJobId(null);
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        uploadMutation.mutate(file);
    };

    const totals = useMemo(() => {
        if (!preview) return { updates: 0, creates: 0 };
        let u = 0, c = 0;
        preview.rows.forEach((r, idx) => {
            if (r.kind === "update" && updateSel[idx]) u += 1;
            if (r.kind === "new" && createCats[idx]) c += 1;
        });
        return { updates: u, creates: c };
    }, [preview, updateSel, createCats]);

    const applyChanges = () => {
        if (!preview) return;
        const payload = { updates: [], creates: [] };
        preview.rows.forEach((r, idx) => {
            if (r.kind === "update" && updateSel[idx]) {
                // Send the full pdf row so the server can apply all diffs
                payload.updates.push({
                    id: r.existing_id,
                    data: {
                        code: r.pdf.code,
                        article: r.pdf.article,
                        provenance: r.pdf.provenance,
                        prix_kg_ht: r.pdf.prix_kg_ht,
                        unite: r.pdf.unite,
                    },
                });
            }
            if (r.kind === "new" && createCats[idx]) {
                payload.creates.push({
                    category_id: createCats[idx],
                    data: {
                        code: r.pdf.code,
                        article: r.pdf.article,
                        provenance: r.pdf.provenance,
                        prix_kg_ht: r.pdf.prix_kg_ht,
                        unite: r.pdf.unite,
                    },
                });
            }
        });
        if (payload.updates.length === 0 && payload.creates.length === 0) {
            toast.error("Aucune ligne sélectionnée à appliquer.");
            return;
        }
        applyMutation.mutate(payload);
    };

    if (!open) return null;

    const busy = uploadMutation.isPending || applyMutation.isPending || jobStatus === "pending";

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
            data-testid="tarifs-import-modal"
        >
            <div
                className="bc-card w-full max-w-5xl max-h-[92vh] flex flex-col"
                style={{ padding: "1.5rem 2rem", background: "rgba(15,15,15,0.98)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-2">
                    <div>
                        <p className="bc-label">Import PDF — mise à jour des tarifs</p>
                        <p className="text-xs text-[#A0A0A0] mt-2">
                            Chargez votre liste de prix au format PDF. Le système extrait automatiquement les articles en quelques secondes et propose une prévisualisation avant validation.
                        </p>
                    </div>
                    <button
                        className="text-[#A0A0A0] hover:text-[#D4BC7C]"
                        onClick={close}
                        data-testid="import-close"
                        aria-label="Fermer"
                        disabled={applyMutation.isPending}
                    >
                        <X size={20} />
                    </button>
                </div>

                {!preview && (
                    <div className="mt-6">
                        <label
                            className={`border-2 border-dashed p-8 text-center block transition-colors ${jobStatus === "pending" ? "" : "cursor-pointer"}`}
                            style={{
                                borderColor: file ? "var(--bc-gold)" : "var(--bc-border)",
                                background: file ? "rgba(212,188,124,0.05)" : "#0a0a0a",
                                pointerEvents: jobStatus === "pending" ? "none" : undefined,
                                opacity: jobStatus === "pending" ? 0.7 : 1,
                            }}
                            data-testid="import-dropzone"
                        >
                            {file ? (
                                <div className="flex items-center justify-center gap-3">
                                    <FileText size={28} className="text-[#D4BC7C]" />
                                    <div className="text-left">
                                        <p className="text-sm text-[#F2ECE0]">{file.name}</p>
                                        <p className="text-xs text-[#A0A0A0]">
                                            {(file.size / 1024).toFixed(1)} Ko — PDF prêt à analyser
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload size={26} className="text-[#D4BC7C]" />
                                    <p className="text-sm text-[#F2ECE0]">
                                        Cliquez pour sélectionner votre liste PDF
                                    </p>
                                    <p className="text-[10px] text-[#A0A0A0] uppercase tracking-widest">
                                        Format PDF · 20 Mo max
                                    </p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="application/pdf,.pdf"
                                className="hidden"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="import-file-input"
                                disabled={jobStatus === "pending"}
                            />
                        </label>

                        {jobStatus === "pending" && (
                            <div
                                className="mt-4 p-4 border flex items-center gap-3"
                                style={{ borderColor: "var(--bc-gold)", background: "rgba(212,188,124,0.06)" }}
                                data-testid="import-pending"
                            >
                                <Loader2 size={18} className="animate-spin text-[#D4BC7C] shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm text-[#F2ECE0]">Extraction en cours…</p>
                                    <p className="text-xs text-[#A0A0A0] mt-1">
                                        Analyse du PDF (généralement moins de 5 secondes). Vous pouvez laisser cette fenêtre ouverte.
                                    </p>
                                </div>
                            </div>
                        )}

                        {jobStatus === "error" && jobError && (
                            <div
                                className="mt-4 p-4 border flex items-start gap-3"
                                style={{ borderColor: "#fca5a5", background: "rgba(252,165,165,0.06)" }}
                                data-testid="import-error"
                            >
                                <AlertCircle size={18} className="text-[#fca5a5] shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm text-[#fca5a5]">Extraction impossible</p>
                                    <p className="text-xs text-[#F2ECE0] mt-1">{jobError}</p>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex gap-3 justify-end">
                            <button
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={close}
                                disabled={applyMutation.isPending}
                                data-testid="import-cancel"
                                title={jobStatus === "pending" ? "L'extraction en arrière-plan continuera, ce modal se ferme simplement." : ""}
                            >
                                {jobStatus === "pending" ? "Fermer" : "Annuler"}
                            </button>
                            <button
                                className="bc-btn bc-btn--sm"
                                onClick={startUpload}
                                disabled={busy || !file || jobStatus === "pending"}
                                data-testid="import-analyze"
                            >
                                {jobStatus === "pending" ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" /> Analyse…
                                    </>
                                ) : jobStatus === "error" ? (
                                    <>
                                        <ArrowRight size={14} /> Réessayer
                                    </>
                                ) : (
                                    <>
                                        <ArrowRight size={14} /> Analyser le PDF
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {preview && (
                    <div className="mt-4 flex-1 overflow-hidden flex flex-col">
                        <PreviewSummary preview={preview} totals={totals} />

                        <div className="mt-4 overflow-y-auto flex-1 pr-1">
                            <div className="space-y-2">
                                {preview.rows.map((r, idx) => (
                                    <PreviewRow
                                        key={idx}
                                        row={r}
                                        idx={idx}
                                        selected={r.kind === "update" ? !!updateSel[idx] : !!createCats[idx]}
                                        onToggle={(v) => {
                                            if (r.kind === "update") {
                                                setUpdateSel((prev) => ({ ...prev, [idx]: v }));
                                            }
                                        }}
                                        onSelectCategory={(catId) =>
                                            setCreateCats((prev) => ({ ...prev, [idx]: catId || undefined }))
                                        }
                                        selectedCategory={createCats[idx] || ""}
                                        categories={categories}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t flex flex-wrap gap-3 justify-between items-center" style={{ borderColor: "var(--bc-border)" }}>
                            <button
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={() => { setPreview(null); setFile(null); }}
                                disabled={busy}
                                data-testid="import-restart"
                            >
                                <RefreshCw size={14} /> Recommencer
                            </button>
                            <div className="flex items-center gap-3">
                                <button className="bc-btn bc-btn--sm bc-btn--ghost" onClick={close} disabled={busy}>
                                    Annuler
                                </button>
                                <button
                                    className="bc-btn bc-btn--sm"
                                    onClick={applyChanges}
                                    disabled={busy || (totals.updates + totals.creates === 0)}
                                    data-testid="import-apply"
                                >
                                    {applyMutation.isPending ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" /> Application…
                                        </>
                                    ) : (
                                        <>
                                            <Check size={14} /> Appliquer ({totals.updates + totals.creates})
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatChip({ label, count, color }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border"
            style={{ color, borderColor: color, background: "rgba(255,255,255,0.02)" }}
        >
            {label} : <strong>{count}</strong>
        </span>
    );
}

function PreviewSummary({ preview, totals }) {
    const s = preview.stats;
    return (
        <div
            className="p-4 border"
            style={{ borderColor: "var(--bc-border)", background: "rgba(212,188,124,0.03)" }}
            data-testid="import-summary"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs text-[#A0A0A0] uppercase tracking-widest mr-2">Résultat de l&apos;analyse</p>
                <StatChip label="Extraits" count={preview.extracted_count} color="#F2ECE0" />
                <StatChip label="À mettre à jour" count={s.updated} color="#D4BC7C" />
                <StatChip label="Inchangés" count={s.unchanged} color="#A0A0A0" />
                <StatChip label="Nouveaux" count={s.new} color="#93c5fd" />
            </div>
            <p className="text-[10px] text-[#A0A0A0] mt-3">
                Sélection prête à appliquer : <strong className="text-[#D4BC7C]">{totals.updates}</strong> mise{totals.updates > 1 ? "s" : ""} à jour,{" "}
                <strong className="text-[#93c5fd]">{totals.creates}</strong> création{totals.creates > 1 ? "s" : ""}.
            </p>
        </div>
    );
}

function DiffCell({ diff }) {
    // diff = {from, to}
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px]">
            <span className="text-[#A0A0A0] line-through">{diff.from == null || diff.from === "" ? "—" : String(diff.from)}</span>
            <ArrowRight size={10} className="text-[#D4BC7C]" />
            <span className="text-[#D4BC7C]">{diff.to == null || diff.to === "" ? "—" : String(diff.to)}</span>
        </span>
    );
}

function PreviewRow({ row, idx, selected, onToggle, onSelectCategory, selectedCategory, categories }) {
    const c = KIND_COLOR[row.kind] || KIND_COLOR.unchanged;
    const isUpdate = row.kind === "update";
    const isNew = row.kind === "new";

    return (
        <div
            className="border p-3"
            style={{ borderColor: selected ? c.border : "var(--bc-border)", background: selected ? c.bg : "#0a0a0a" }}
            data-testid={`import-row-${idx}`}
        >
            <div className="flex items-start gap-3">
                {isUpdate && (
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-[#D4BC7C]"
                        checked={selected}
                        onChange={(e) => onToggle(e.target.checked)}
                        aria-label="Appliquer cette mise à jour"
                        data-testid={`import-row-toggle-${idx}`}
                    />
                )}
                {row.kind === "unchanged" && (
                    <Check size={16} className="text-[#A0A0A0] mt-0.5 shrink-0" />
                )}
                {isNew && (
                    <AlertCircle size={16} className="text-[#93c5fd] mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="text-[9px] uppercase tracking-widest px-2 py-0.5 border font-medium"
                            style={{ color: c.color, borderColor: c.border, background: c.bg }}
                        >
                            {KIND_LABEL[row.kind]}
                        </span>
                        <span className="font-mono text-[10px] text-[#A0A0A0]">
                            {row.pdf.code || "—"}
                        </span>
                        <span className="text-sm text-[#F2ECE0] truncate">{row.pdf.article}</span>
                        {row.match_type === "name" && (
                            <span
                                className="text-[9px] uppercase tracking-widest text-[#A0A0A0]"
                                title="Article retrouvé par correspondance du nom (fallback)"
                            >
                                (via nom)
                            </span>
                        )}
                    </div>

                    {isUpdate && (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                            {Object.entries(row.changes).map(([field, diff]) => (
                                <div key={field} className="flex items-center gap-2 text-[11px]">
                                    <span className="text-[#A0A0A0] uppercase tracking-widest w-[70px]">{field}</span>
                                    <DiffCell diff={diff} />
                                </div>
                            ))}
                        </div>
                    )}

                    {row.kind === "unchanged" && (
                        <p className="text-[11px] text-[#A0A0A0] mt-1">
                            Prix actuel : <span className="text-[#F2ECE0]">{formatPrice(row.existing?.prix_kg_ht, row.existing?.unite)}</span> — aucun changement détecté.
                        </p>
                    )}

                    {isNew && (
                        <div className="mt-2 space-y-2">
                            <p className="text-[11px] text-[#A0A0A0]">
                                Introuvable dans le catalogue actuel. Prix PDF :{" "}
                                <span className="text-[#F2ECE0]">{formatPrice(row.pdf.prix_kg_ht, row.pdf.unite)}</span>
                                {row.pdf.provenance && <> · Provenance : <span className="text-[#F2ECE0]">{row.pdf.provenance}</span></>}
                                {row.pdf.categorie && <> · <span className="text-[#F2ECE0]">Section PDF : {row.pdf.categorie}</span></>}
                            </p>
                            <div className="flex items-center gap-2">
                                <label className="text-[10px] text-[#A0A0A0] uppercase tracking-widest">
                                    Créer dans :
                                </label>
                                <select
                                    className="bc-input"
                                    style={{ padding: "0.35rem 0.6rem", fontSize: "0.85rem", flex: 1, maxWidth: 260 }}
                                    value={selectedCategory}
                                    onChange={(e) => onSelectCategory(e.target.value)}
                                    data-testid={`import-row-category-${idx}`}
                                >
                                    <option value="">— Ignorer (ne pas créer) —</option>
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.nom}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
